import { BaseService } from '@colescba/services'
import { MD5 } from 'crypto-js'

const authHeaders = cuitCuil => {
  return {
    id: Buffer.from(cuitCuil).toString('base64'),
    token: MD5(cuitCuil).toString(),
  }
}

const isLocalPortalPagos = () => {
  const url = (process.env.VUE_APP_PORTAL_PAGOS_SERVICE_URL || '').toLowerCase()
  return url.includes('localhost')
}

const localUserCuitHeader = cuitCuil => {
  if (!isLocalPortalPagos()) return {}
  const fallback =
    process.env.VUE_APP_PORTAL_PAGOS_LOCAL_USER_CUIT || process.env.VUE_APP_LOCAL_USER_CUIT
  const value = cuitCuil || fallback
  if (!value) return {}
  return { 'x-colescba-user-cuit': value.toString() }
}

const buildHeaders = cuitCuil => {
  const headers = { ...localUserCuitHeader(cuitCuil) }
  if (cuitCuil) {
    return { ...headers, ...authHeaders(cuitCuil) }
  }
  return headers
}

const buildFilterString = object => {
  return Object.keys(object)
    .map(key => `${key}:${object[key]}`)
    .join(',')
}

export class PortalPagosService extends BaseService {
  /**
   *
   * @param { String } cuitCuil
   * @param { String } filter
   * @param { String } sort
   */
  async getDeudas(cuitCuil, filter, sort) {
    const response = await this.http.get(`/deudas`, {
      headers: buildHeaders(cuitCuil),
      params: {
        filter: filter,
        sort: sort,
      },
    })
    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { Object } params
   * @param { String } params.tipo
   * @param { Number } params.importe
   * @param { String } params.titular_cuit_cuil
   * @param { String } params.nombres
   * @param { String } params.apellido
   * @param { String[] } params.medios_pago
   */
  async postDeuda(cuitCuil, params) {
    /*{
      "tipo":"752",
      "importe":102.5,
      "titular_cuit_cuil":"20292297261",
      "nombres":"Adrian",
      "apellido":"Lopez",
      "medios_pago":["LINK"]
    }*/

    const response = await this.http.post(`/deudas`, params, {
      headers: buildHeaders(cuitCuil),
    })

    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { String } tipoDeuda
   */
  async getMediosPago(cuitCuil, tipoDeuda) {
    const response = await this.http.get(`/medios-pago`, {
      headers: buildHeaders(cuitCuil),
      params: {
        tipo: tipoDeuda,
      },
    })
    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   */
  async getSaldo(cuitCuil) {
    const response = await this.http.get(`/cuentas/saldo`, {
      headers: buildHeaders(cuitCuil),
    })
    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { Number } idDeuda
   */

  async getComprobantes(cuitCuil, params) {
    const response = await this.http.get(`/comprobantes`, {
      headers: buildHeaders(cuitCuil),
      params: {
        filter: buildFilterString({
          filter_name: 'FN_CMPTPagoPendiente',
          ...params,
        }),
      },
    })

    return response.data
  }
  async getDeuda(cuitCuil, idDeuda) {
    const response = await this.http.get(`/deudas/${idDeuda}`, {
      headers: buildHeaders(cuitCuil),
    })
    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { Number } idDeuda
   * @param { Object } params
   * @param { String[] } params.medios_pago
   */
  async putDeuda(cuitCuil, idDeuda, params) {
    const response = await this.http.put(`/deudas/${idDeuda}`, params, {
      headers: buildHeaders(cuitCuil),
    })

    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { Number|String } nroComprobante
   */
  async getComprobante(cuitCuil, nroComprobante) {
    const response = await this.http.get(`/comprobantes/${nroComprobante}`, {
      headers: buildHeaders(cuitCuil),
    })
    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { Number|String } nroComprobante
   */
  async getRecibo(cuitCuil, nroComprobante) {
    const response = await this.http.get(`/recibos/${nroComprobante}`, {
      headers: buildHeaders(cuitCuil),
    })
    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { Number|String } nroComprobante
   */
  async getComprobanteTerceros(cuitCuil, nroComprobante) {
    const response = await this.http.get(`/comprobantes-terceros/${nroComprobante}`, {
      headers: buildHeaders(cuitCuil),
    })
    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { String } params.fechaDesde fecha en formato DD-MM-YYYY
   * @param { String } params.fechaHasta fecha en formato DD-MM-YYYY
   * @param { Number } params.montoDesde
   * @param { Number } params.montoHasta
   */
  async getFacturas(cuitCuil, params) {
    const response = await this.http.get(`/facturas`, {
      headers: buildHeaders(cuitCuil),
      params: {
        filter: buildFilterString({
          filter_name: 'FN_FacturaTerceros',
          fechaDesde: params.fechaDesde,
          fechaHasta: params.fechaHasta,
          montoDesde: params.montoDesde,
          montoHasta: params.montoHasta,
        }),
      },
    })
    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { String } nroComprobante
   * @param { Object } params
   * @param { String } params.uso_id
   * @param { String } params.factura_codigo
   * @param { String } params.factura_numero
   * @param { String } params.observaciones
   */
  async patchUsoComprobante(cuitCuil, nroComprobante, params) {
    const response = await this.http.patch(`/comprobantes-terceros/${nroComprobante}/uso`, params, {
      headers: buildHeaders(cuitCuil),
    })

    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   */
  async getSedes(cuitCuil) {
    const response = await this.http.get(`/adm/sedes`, {
      headers: buildHeaders(cuitCuil),
    })
    return response.data
  }

  async getHtmlMacroClick(cuitCuil, deudaId) {
    const response = await this.http.get(`/macro-click`, {
      headers: buildHeaders(cuitCuil),
      params: {
        deudaId: deudaId,
      },
    })
    return response.data
  }

  async createProvinciaNetPayment(cuitCuil, deudaId) {
    const response = await this.http.post(
      `/provincianet/deudas/${deudaId}/payments`,
      {},
      {
        headers: {
          ...buildHeaders(cuitCuil),
          'Content-Type': 'application/json',
        },
      },
    )
    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { Object } params
   * @param { String } params.factu_codi
   * @param { String|Number } params.factu_nume
   * @param { String } params.uso_id
   * @param { Number } params.sede_id
   */
  async findComprobantesPorFactura(cuitCuil, params) {
    const response = await this.http.get(`/comprobantes-terceros`, {
      headers: buildHeaders(cuitCuil),
      params: {
        filter: buildFilterString({
          filter_name: 'FNComprobantesXCodYNroFactura',
          ...params,
        }),
      },
    })
    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { Object } params
   * @param { String } params.pago_id
   * @param { String } params.uso_id
   * @param { String } params.fecha_desde
   * @param { String } params.fecha_hasta
   * @param { String } params.fecha_venc_desde
   * @param { String } params.fecha_venc_hasta
   * @param { Number } params.sede_id
   */
  async findComprobantesPorUsoYPago(cuitCuil, params) {
    const response = await this.http.get(`/comprobantes-terceros`, {
      headers: buildHeaders(cuitCuil),
      params: {
        filter: buildFilterString({
          filter_name: 'FNComprobantesXUsoYPago',
          ...params,
        }),
      },
    })
    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { String } nroComprobante
   * @param { Object } params
   * @param { String } params.pago_id
   * @param { String } params.forma_pago_id
   * @param { String } params.observaciones
   */
  async patchPagoPorComprobante(cuitCuil, nroComprobante, params) {
    const response = await this.http.patch(
      `/comprobantes-terceros/${nroComprobante}/pago`,
      params,
      {
        headers: buildHeaders(cuitCuil),
      },
    )

    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { Object } params
   * @param { String } params.pago_id
   * @param { String } params.forma_pago_id
   * @param { String } params.factura_codigo
   * @param { String } params.factura_numero
   */
  async patchPagoPorFactura(cuitCuil, params) {
    const response = await this.http.patch(`/comprobantes-terceros/pago`, params, {
      headers: buildHeaders(cuitCuil),
    })

    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { String } nroComprobante
   * @param { Object } params
   * @param { String } params.tipo
   * @param { String } params.sede_id
   */
  async patchComprobante(cuitCuil, nroComprobante, params) {
    const response = await this.http.patch(`/comprobantes-terceros/${nroComprobante}`, params, {
      headers: buildHeaders(cuitCuil),
    })

    return response.data
  }

  async getEstadosUso(cuitCuil) {
    const response = await this.http.get(`/comprobantes-terceros/estados-uso`, {
      headers: buildHeaders(cuitCuil),
    })
    return response.data
  }

  async getEstadosPago(cuitCuil) {
    const response = await this.http.get(`/comprobantes-terceros/estados-pago`, {
      headers: buildHeaders(cuitCuil),
    })
    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { Object } params
   * @param { Number } params.sede_id
   * @param { Number } params.oficina_id
   */
  async getSedes(cuitCuil, params) {
    const response = await this.http.get(`/adm/sedes`, {
      headers: buildHeaders(cuitCuil),
      params: params,
    })
    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { Object } params
   * @param { String } params.sede_id
   * @param { String } params.fecha_desde
   * @param { String } params.fecha_hasta
   */
  async findFacturasReporte(cuitCuil, params) {
    const response = await this.http.get(`/comprobantes-terceros/reportes`, {
      headers: buildHeaders(cuitCuil),
      params: {
        filter: buildFilterString({
          filter_name: 'FNComprobantesXUsadosPorFactura',
          ...params,
        }),
      },
    })
    return response.data
  }

  /**
   *
   * @param { String } cuitCuil
   * @param { Object } params
   * @param { String } params.sede_id
   * @param { String } params.fecha_desde
   * @param { String } params.fecha_hasta
   */
  async findFacturasReporteCsv(cuitCuil, params) {
    const response = await this.http.get(`/comprobantes-terceros/reportes/csv`, {
      headers: buildHeaders(cuitCuil),
      responseType: 'arraybuffer',
      params: {
        filter: buildFilterString({
          filter_name: 'FNComprobantesXUsadosPorFactura',
          ...params,
        }),
      },
    })
    return response.data
  }

  async getRadicaciones() {
    const response = await this.http.get(`/radicaciones`, { headers: buildHeaders() })
    return response.data
  }

  async getPartidos() {
    const response = await this.http.get(`/partidos`, { headers: buildHeaders() })
    return response.data
  }

  async getDepartamentosJudiciales(partidoId) {
    const response = await this.http.get(
      `/departamentos_judiciales` + (partidoId ? `?partido_id=${partidoId}` : ''),
      { headers: buildHeaders() },
    )
    return response.data
  }

  async getFueros(radicacionId) {
    const response = await this.http.get(`/fueros_radicacion?radicacion_id=${radicacionId}`, {
      headers: buildHeaders(),
    })
    return response.data
  }

  async getOrganismos() {
    const response = await this.http.get(`/organismos`, { headers: buildHeaders() })
    return response.data
  }

  async getEstadosDeuda() {
    const response = await this.http.get(`/deuda-estados?detalle`, { headers: buildHeaders() })
    return response.data
  }

  async getProductos() {
    const response = await this.http.get(`/productos`, { headers: buildHeaders() })
    return response.data
  }

  async getParametrosTipoDeuda(tipoDeuda) {
    const response = await this.http.get(`/deuda-tipos/${tipoDeuda}/parametros`, {
      headers: buildHeaders(),
    })
    return response.data
  }

  async getTiposHonorario() {
    const response = await this.http.get(`/honorario_tipos`, { headers: buildHeaders() })
    return response.data
  }

  async getInstanciasRegulacion() {
    const response = await this.http.get(`/regulacion_instancias`, { headers: buildHeaders() })
    return response.data
  }

  async getTiposDeuda() {
    const response = await this.http.get(`/deuda-tipos`, { headers: buildHeaders() })
    return response.data
  }

  // Endpoints para formularios DPPJ

  async getDppjDelegaciones() {
    const response = await this.http.get(`/dppj/delegaciones`, { headers: buildHeaders() })
    return response.data
  }

  async getDppjGrupos() {
    const response = await this.http.get(`/dppj/grupos`, { headers: buildHeaders() })
    return response.data
  }

  async getDppjTramites(grupoId) {
    const response = await this.http.get(`/dppj/tramites?grupo_id=${grupoId}`, {
      headers: buildHeaders(),
    })
    return response.data
  }

  async getDppjPrecios(grupoId) {
    const response = await this.http.get(`/dppj/precios?grupo_id=${grupoId}`, {
      headers: buildHeaders(),
    })
    return response.data
  }

  async getDppjRangoPrecios(grupoId) {
    const response = await this.http.get(`/dppj/rango-precios?grupo_id=${grupoId}`, {
      headers: buildHeaders(),
    })
    return response.data
  }

  async getDppjRazonSocial(nroLegajo) {
    const response = await this.http.get(`/dppj/razon-social?numero_legajo=${nroLegajo}`, {
      headers: buildHeaders(),
    })
    return response.data
  }

  // Endpoints para formulario Honorarios Judiciales

  async getHjImportesComprobante(honorarios) {
    const response = await this.http.get(
      `/honorariosjudiciales/comprobante_importes?honorarios=${honorarios}`,
      { headers: buildHeaders() },
    )
    return response.data
  }
}
